import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Chip,
  Tooltip,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  AutoAwesome as AutoIcon,
  Sync as SyncIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import apiService from '../services/api';

// 정렬 가능한 컬럼 정의 (단일 VM 상세 페이지: vm_name 컬럼 없음)
const SECTION_SORTABLE_COLUMNS = [
  { id: 'protocol', label: '프로토콜', numeric: false },
  { id: 'host_port', label: '호스트 포트', numeric: true },
  { id: 'vm_port', label: 'VM 포트', numeric: true },
  { id: 'description', label: '설명', numeric: false },
];

const compareValues = (a, b, orderBy) => {
  const av = a?.[orderBy];
  const bv = b?.[orderBy];
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  if (typeof av === 'number' && typeof bv === 'number') return av - bv;
  return String(av).localeCompare(String(bv), undefined, { numeric: true });
};

// 알려진 프로토콜 정의
const KNOWN_PROTOCOLS = {
  ssh: { port: 22, protocol: 'tcp', label: 'SSH' },
  http: { port: 80, protocol: 'tcp', label: 'HTTP' },
  https: { port: 443, protocol: 'tcp', label: 'HTTPS' },
  rdp: { port: 3389, protocol: 'tcp', label: 'RDP' },
  vnc: { port: 5900, protocol: 'tcp', label: 'VNC' },
  mysql: { port: 3306, protocol: 'tcp', label: 'MySQL' },
  postgresql: { port: 5432, protocol: 'tcp', label: 'PostgreSQL' },
  mongodb: { port: 27017, protocol: 'tcp', label: 'MongoDB' },
  redis: { port: 6379, protocol: 'tcp', label: 'Redis' },
  custom: { port: '', protocol: 'tcp', label: 'Custom' }
};

const PortForwardingSection = ({ vmName, isNATNetwork, vmActive }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProtocol, setSelectedProtocol] = useState('ssh');
  const [autoMode, setAutoMode] = useState(false);
  const [portConfig, setPortConfig] = useState({ min_port: 10000, max_port: 65535 });
  const [usedPorts, setUsedPorts] = useState([]);
  const [formData, setFormData] = useState({
    protocol: 'tcp',
    host_port: '',
    vm_port: 22,
    description: 'SSH'
  });

  // 정렬 상태 (기본: 호스트 포트 ASC)
  const [orderBy, setOrderBy] = useState('host_port');
  const [order, setOrder] = useState('asc');

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedRules = useMemo(() => {
    const arr = [...rules];
    arr.sort((a, b) => {
      const cmp = compareValues(a, b, orderBy);
      if (cmp !== 0) return order === 'asc' ? cmp : -cmp;
      // 보조 정렬 키
      return compareValues(a, b, 'host_port');
    });
    return arr;
  }, [rules, orderBy, order]);

  // 포트 범위 설정 로드
  const loadPortConfig = async () => {
    try {
      const response = await apiService.getPortForwardingConfig();
      setPortConfig(response.data);
    } catch (error) {
      console.error('포트 범위 설정 로드 실패:', error);
    }
  };

  // 사용 중인 포트 로드
  const loadUsedPorts = async () => {
    try {
      const response = await apiService.getUsedPorts();
      setUsedPorts(response.data.used_ports || []);
    } catch (error) {
      console.error('사용 중인 포트 로드 실패:', error);
    }
  };

  // 포트 포워딩 규칙 로드
  const loadPortForwards = async () => {
    if (!isNATNetwork) return;

    setLoading(true);
    try {
      const response = await apiService.getVMPortForwards(vmName);
      setRules(response.data.rules || []);
    } catch (error) {
      if (error.response?.status !== 400) {
        enqueueSnackbar(
          `포트 포워딩 규칙 로드 실패: ${error.response?.data?.detail || error.message}`,
          { variant: 'error' }
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPortConfig();
    loadUsedPorts();
  }, []);

  useEffect(() => {
    if (isNATNetwork && vmActive) {
      loadPortForwards();
    }
  }, [vmName, isNATNetwork, vmActive]);

  // 알려진 프로토콜 선택 처리
  const handleProtocolSelect = (protocolKey) => {
    setSelectedProtocol(protocolKey);
    const protocol = KNOWN_PROTOCOLS[protocolKey];

    if (protocolKey === 'custom') {
      setFormData({
        protocol: 'tcp',
        host_port: '',
        vm_port: '',
        description: ''
      });
    } else {
      setFormData({
        protocol: protocol.protocol,
        host_port: '',
        vm_port: protocol.port,
        description: protocol.label
      });
    }
  };

  // 포트가 범위 내에 있는지 확인
  const isPortInRange = (port) => {
    return port >= portConfig.min_port && port <= portConfig.max_port;
  };

  // 포트가 이미 사용 중인지 확인
  const isPortUsed = (port) => {
    return usedPorts.includes(port);
  };

  // 포트 포워딩 규칙 추가
  const handleAddRule = async () => {
    if (!formData.vm_port) {
      enqueueSnackbar('VM 포트를 입력하세요.', { variant: 'warning' });
      return;
    }

    if (!autoMode && !formData.host_port) {
      enqueueSnackbar('호스트 포트를 입력하세요.', { variant: 'warning' });
      return;
    }

    try {
      if (autoMode) {
        // 자동 할당 모드
        const response = await apiService.autoAssignPort({
          vm_name: vmName,
          protocol: formData.protocol,
          vm_port: formData.vm_port,
          description: formData.description || null,
        });
        enqueueSnackbar(response.data.message, { variant: 'success' });
      } else {
        // 수동 할당 모드
        await apiService.createPortForward(vmName, formData);
        enqueueSnackbar('포트 포워딩 규칙이 추가되었습니다.', { variant: 'success' });
      }

      setDialogOpen(false);
      loadPortForwards();
      loadUsedPorts();

      // 폼 초기화
      setSelectedProtocol('ssh');
      setAutoMode(false);
      setFormData({
        protocol: 'tcp',
        host_port: '',
        vm_port: 22,
        description: 'SSH'
      });
    } catch (error) {
      enqueueSnackbar(
        `포트 포워딩 규칙 추가 실패: ${error.response?.data?.detail || error.message}`,
        { variant: 'error' }
      );
    }
  };

  // 포트 포워딩 규칙 삭제
  const handleDeleteRule = async (protocol, hostPort) => {
    if (!window.confirm(`포트 포워딩 규칙 ${protocol}/${hostPort}을(를) 삭제하시겠습니까?`)) {
      return;
    }

    try {
      await apiService.deletePortForward(vmName, protocol, hostPort);
      enqueueSnackbar('포트 포워딩 규칙이 삭제되었습니다.', { variant: 'success' });
      loadPortForwards();
    } catch (error) {
      enqueueSnackbar(
        `포트 포워딩 규칙 삭제 실패: ${error.response?.data?.detail || error.message}`,
        { variant: 'error' }
      );
    }
  };

  // VM IP 동기화
  const handleSyncIP = async () => {
    if (!window.confirm(`VM "${vmName}"의 포트 포워딩 규칙을 현재 IP로 동기화하시겠습니까?\n\n네트워크 변경 후 포트포워딩이 작동하지 않을 때 사용하세요.`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.syncVMPortForwardingIP(vmName);
      const { updated_count, current_ip } = response.data;

      if (updated_count > 0) {
        enqueueSnackbar(
          `${response.data.message}\n현재 IP: ${current_ip}`,
          { variant: 'success', style: { whiteSpace: 'pre-line' } }
        );
      } else {
        enqueueSnackbar('동기화할 규칙이 없거나 모든 규칙이 최신 상태입니다', { variant: 'info' });
      }

      loadPortForwards();
      loadUsedPorts();
    } catch (error) {
      enqueueSnackbar(
        `IP 동기화 실패: ${error.response?.data?.detail || error.message}`,
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  // NAT 네트워크가 아니면 표시하지 않음
  if (!isNATNetwork) {
    return null;
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          포트 포워딩 관리
        </Typography>
        <Box>
          <Tooltip title="IP 동기화">
            <IconButton
              onClick={handleSyncIP}
              disabled={loading || !vmActive || rules.length === 0}
              color="primary"
            >
              <SyncIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="새로고침">
            <IconButton onClick={loadPortForwards} disabled={loading || !vmActive}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            disabled={loading || !vmActive}
          >
            규칙 추가
          </Button>
        </Box>
      </Box>

      {!vmActive && (
        <Alert severity="info" sx={{ mb: 2 }}>
          포트 포워딩 규칙을 관리하려면 VM을 먼저 시작하세요.
        </Alert>
      )}

      <Card>
        <CardContent>
          {sortedRules.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                설정된 포트 포워딩 규칙이 없습니다.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 56 }}>#</TableCell>
                    {SECTION_SORTABLE_COLUMNS.map((col) => (
                      <TableCell
                        key={col.id}
                        sortDirection={orderBy === col.id ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? order : 'asc'}
                          onClick={() => handleRequestSort(col.id)}
                          IconComponent={
                            orderBy === col.id
                              ? order === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon
                              : undefined
                          }
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell align="center">작업</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRules.map((rule, index) => (
                    <TableRow
                      key={`${rule.protocol}-${rule.host_port}`}
                      hover
                    >
                      <TableCell sx={{ color: 'text.secondary' }}>{index + 1}</TableCell>
                      <TableCell>
                        <Chip label={rule.protocol.toUpperCase()} size="small" />
                      </TableCell>
                      <TableCell>{rule.host_port}</TableCell>
                      <TableCell>{rule.vm_port}</TableCell>
                      <TableCell>{rule.description || '-'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="삭제">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteRule(rule.protocol, rule.host_port)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* 규칙 추가 다이얼로그 */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>포트 포워딩 규칙 추가</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoMode}
                  onChange={(e) => setAutoMode(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <AutoIcon fontSize="small" />
                  자동 포트 할당
                </Box>
              }
              sx={{ mb: 2 }}
            />

            <TextField
              select
              fullWidth
              label="프로토콜 선택"
              value={selectedProtocol}
              onChange={(e) => handleProtocolSelect(e.target.value)}
              sx={{ mb: 2 }}
            >
              {Object.entries(KNOWN_PROTOCOLS).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value.label} {value.port && `(기본 포트: ${value.port})`}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              label="전송 프로토콜"
              value={formData.protocol}
              onChange={(e) => setFormData({ ...formData, protocol: e.target.value })}
              sx={{ mb: 2 }}
            >
              <MenuItem value="tcp">TCP</MenuItem>
              <MenuItem value="udp">UDP</MenuItem>
            </TextField>

            {!autoMode && (
              <TextField
                fullWidth
                type="number"
                label="호스트 포트"
                value={formData.host_port}
                onChange={(e) => setFormData({ ...formData, host_port: parseInt(e.target.value) || '' })}
                helperText={
                  formData.host_port && isPortUsed(formData.host_port)
                    ? '이미 사용 중인 포트입니다'
                    : formData.host_port && !isPortInRange(formData.host_port)
                    ? `포트 범위: ${portConfig.min_port}-${portConfig.max_port}`
                    : `설정된 범위: ${portConfig.min_port}-${portConfig.max_port}`
                }
                error={
                  formData.host_port &&
                  (isPortUsed(formData.host_port) || !isPortInRange(formData.host_port))
                }
                sx={{ mb: 2 }}
                inputProps={{ min: portConfig.min_port, max: portConfig.max_port }}
              />
            )}

            <TextField
              fullWidth
              type="number"
              label="VM 포트"
              value={formData.vm_port}
              onChange={(e) => setFormData({ ...formData, vm_port: parseInt(e.target.value) || '' })}
              helperText="VM 내부의 서비스 포트"
              sx={{ mb: 2 }}
              inputProps={{ min: 1, max: 65535 }}
            />

            <TextField
              fullWidth
              label="설명 (선택사항)"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              helperText="규칙을 식별하기 위한 설명"
            />

            {autoMode && (
              <Alert severity="info" sx={{ mt: 2 }}>
                자동 모드: 설정된 범위({portConfig.min_port}-{portConfig.max_port}) 내에서 사용 가능한 포트가 자동으로
                할당됩니다.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>취소</Button>
          <Button
            onClick={handleAddRule}
            variant="contained"
            disabled={
              !formData.vm_port ||
              (!autoMode && !formData.host_port) ||
              (!autoMode && formData.host_port && isPortUsed(formData.host_port)) ||
              (!autoMode && formData.host_port && !isPortInRange(formData.host_port))
            }
          >
            추가
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortForwardingSection;
